import { useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import { useTranslation } from "react-i18next";
import type { PriceEnvelopeDay, PriceEnvelopeShop } from "@/types";

interface Props {
  envelope: PriceEnvelopeDay[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const SPARSE_THRESHOLD = 10;

function formatEuro(n: number) {
  return `${n.toFixed(2)} €`;
}

function formatDate(iso: string | number) {
  return new Date(iso).toLocaleDateString("lt-LT", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface EnvelopeRow {
  x: number;
  min: number;
  max: number;
  med: number;
  shops: PriceEnvelopeShop[];
}

// Lift the server envelope into Recharts-friendly rows (numeric x for the
// time-scale axis). The server already appends a today row when nothing was
// crawled today, so we never synthesize a phantom point client-side.
function useEnvelopeRows(envelope: PriceEnvelopeDay[]): EnvelopeRow[] {
  return useMemo(
    () =>
      envelope.map((d) => {
        const date = new Date(d.date);
        return {
          x: Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
          ),
          min: d.min,
          max: d.max,
          med: d.med,
          shops: d.shops,
        };
      }),
    [envelope],
  );
}

function getXDomain(rows: EnvelopeRow[]): [number, number] {
  if (rows.length === 0) return [0, 1];
  const xs = rows.map((r) => r.x);
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  if (max - min < DAY_MS / 24) {
    return [min - DAY_MS / 2, max + DAY_MS / 2];
  }
  return [min, max];
}

// Detect significant price drops (≥5%) between consecutive datapoints. We pin a
// ReferenceDot at each so users can see *when* the floor moved.
interface DropAnnotation {
  x: number;
  y: number;
  pct: number;
}

function findDrops(rows: EnvelopeRow[]): DropAnnotation[] {
  const drops: DropAnnotation[] = [];
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1].min;
    const curr = rows[i].min;
    if (prev > 0 && curr < prev) {
      const pct = Math.round(((prev - curr) / prev) * 100);
      if (pct >= 5) {
        drops.push({ x: rows[i].x, y: curr, pct });
      }
    }
  }
  return drops;
}

export function PriceTrendChart({ envelope }: Props) {
  const { t } = useTranslation();
  const data = useEnvelopeRows(envelope);
  const [xMin, xMax] = useMemo(() => getXDomain(data), [data]);
  const drops = useMemo(() => findDrops(data), [data]);
  const isSparse = data.length < SPARSE_THRESHOLD;
  const [selectedX, setSelectedX] = useState<number | null>(null);

  if (data.length < 2) return null;

  const chartHeight = isSparse ? 160 : 280;
  const selectedRow =
    selectedX != null ? data.find((r) => r.x === selectedX) ?? null : null;

  const handleChartClick = (state: any) => {
    // Recharts 3.x onClick receives MouseHandlerDataParam — activeLabel holds
    // the x-axis value of the clicked tick (numeric, since XAxis type="number").
    const label = state?.activeLabel;
    const idx = state?.activeTooltipIndex ?? state?.activeIndex;
    let x: number | null = null;
    if (typeof label === "number") x = label;
    else if (typeof idx === "number" && data[idx]) x = data[idx].x;
    if (x != null) setSelectedX((prev) => (prev === x ? null : x));
  };

  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-gray-500">
          {t("price_history.section_heading")}
        </p>
        {isSparse && (
          <p className="text-[11px] text-gray-400">
            {data.length}{" "}
            {t("price_history.summary_observations", { count: data.length }).replace(/^\d+\s/, "")}
          </p>
        )}
      </div>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={data}
          margin={{ top: 14, right: 20, bottom: 10, left: 10 }}
          onClick={handleChartClick}
          style={{ cursor: "pointer" }}
        >
          <defs>
            <linearGradient id="floorWineFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#9f1239" stopOpacity={0.2} />
              <stop offset="50%" stopColor="#fb7185" stopOpacity={0.07} />
              <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
            </linearGradient>
          </defs>
          {!isSparse && (
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
          )}
          <XAxis
            type="number"
            dataKey="x"
            domain={[xMin, xMax]}
            tickFormatter={formatDate}
            tick={{ fontSize: 11, fill: "#525252" }}
            stroke="#e5e5e5"
            hide={isSparse}
          />
          <YAxis
            type="number"
            tickFormatter={(v) => `${Math.round(v)}€`}
            tick={{ fontSize: 11, fill: "#525252" }}
            stroke="#e5e5e5"
            hide={isSparse}
          />
          <Tooltip content={<EnvelopeTooltip />} />
          <Area
            type="linear"
            dataKey="min"
            stroke="#881337"
            strokeWidth={2.5}
            fill="url(#floorWineFill)"
            dot={isSparse ? { r: 3, fill: "#be123c", stroke: "none" } : { r: 3.5, fill: "#be123c", stroke: "none" }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
          />
          {!isSparse && (
            <Line
              type="linear"
              dataKey="med"
              stroke="#a3a3a3"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          )}
          {!isSparse && (
            <Line
              type="linear"
              dataKey="max"
              stroke="#e5e5e5"
              strokeWidth={1}
              strokeDasharray="2 4"
              dot={false}
              isAnimationActive={false}
            />
          )}
          {/* Drop annotations — small emerald dots with arrow labels mark
              meaningful price drops in the floor line. */}
          {drops.map((d, i) => (
            <ReferenceDot
              key={`drop-${i}`}
              x={d.x}
              y={d.y}
              r={6}
              fill="#059669"
              stroke="#ffffff"
              strokeWidth={2}
              label={{
                value: `↓${d.pct}%`,
                position: "top",
                fill: "#047857",
                fontSize: 10,
                fontWeight: 600,
              }}
              isFront
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      {selectedRow ? (
        <SelectedDayPanel
          row={selectedRow}
          onClose={() => setSelectedX(null)}
        />
      ) : (
        <p className="mt-2 text-[11px] text-gray-400">
          {t("price_history.click_dot_hint")}
        </p>
      )}
    </div>
  );
}

interface SelectedDayPanelProps {
  row: EnvelopeRow;
  onClose: () => void;
}

function SelectedDayPanel({ row, onClose }: SelectedDayPanelProps) {
  const { t } = useTranslation();
  const cheapest = row.shops[0]?.price;
  return (
    <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50/60 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-xs font-medium text-gray-700">
          {t("price_history.selected_day_heading", { date: formatDate(row.x) })}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-gray-500 hover:text-gray-700"
        >
          {t("price_history.close")}
        </button>
      </div>
      <ul className="divide-y divide-gray-200">
        {row.shops.map((shop) => {
          const isCheapest = shop.price === cheapest;
          return (
            <li
              key={shop.shopName}
              className="flex items-center justify-between gap-3 py-1.5 text-xs"
            >
              <span className="flex items-center gap-2">
                {isCheapest && (
                  <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-900">
                    {t("price_history.cheapest_badge")}
                  </span>
                )}
                {shop.listingUrl ? (
                  <a
                    href={shop.listingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-800 hover:text-rose-900 hover:underline"
                  >
                    {shop.shopName}
                  </a>
                ) : (
                  <span className="text-gray-800">{shop.shopName}</span>
                )}
              </span>
              <span
                className={
                  isCheapest
                    ? "font-semibold text-rose-900"
                    : "text-gray-600"
                }
              >
                {formatEuro(shop.price)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EnvelopeTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="text-rose-900">Min: {formatEuro(row.min)}</p>
      <p className="text-gray-600">Mediana: {formatEuro(row.med)}</p>
      <p className="text-gray-400">Max: {formatEuro(row.max)}</p>
      <p className="mt-0.5 text-gray-400">{formatDate(row.x)}</p>
    </div>
  );
}
